import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
/**
 * Token linter for the contract Template Editor.
 *
 * Catches the two classes of problems that used to surface only on the
 * generated PDF:
 *   1. malformed tokens (single braces, unclosed `{{`, empty `{{}}`,
 *      hidden rich-text markup or invisible characters inside braces)
 *   2. unknown tokens (namespace or field that no resolver provides)
 */

const RUNTIME_TOKENS = new Set(['page', 'pages', 'mm', 'yy', 'yyyy', 'seq']);
const TOKEN_SPAN = /\{\{([\s\S]{0,300}?)\}\}/g;
const INVISIBLE = /[\u200b-\u200d\u2060\ufeff\u00ad\u00a0]/;

export const collectKnownKeys = (catalogue) => {
  const keys = new Set();
  Object.values(catalogue || {}).forEach((list) => (list || []).forEach((k) => keys.add(k)));
  return keys;
};

const namespaceExists = (ns, knownKeys) => {
  const prefix = `${ns}.`;
  for (const k of knownKeys) if (k.startsWith(prefix)) return true;
  return false;
};

export const lintText = (text, knownKeys, where) => {
  const issues = [];
  if (!text) return issues;

  let m;
  TOKEN_SPAN.lastIndex = 0;
  while ((m = TOKEN_SPAN.exec(text)) !== null) {
    const rawInner = m[1];
    const inner = rawInner
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/[\u200b-\u200d\u2060\ufeff\u00ad]/g, '')
      .trim();
    const dirty = /<[^>]+>/.test(rawInner) || /&[a-z#0-9]+;/i.test(rawInner) || INVISIBLE.test(rawInner);

    if (!inner) {
      issues.push({ where, token: '{{}}', level: 'error', message: 'Empty token — nothing will print' });
      continue;
    }
    const base = inner.split(':')[0].trim();
    if (!/^[a-zA-Z_][a-zA-Z0-9_.]*$/.test(base)) {
      issues.push({ where, token: `{{${inner}}}`, level: 'error', message: 'Not a valid token name' });
      continue;
    }
    if (dirty) {
      issues.push({
        where, token: `{{${base}}}`, level: 'warn',
        message: 'Hidden formatting inside the braces — retype it as plain text',
      });
    }
    const ns = base.split('.')[0];
    if (ns === 'input' || RUNTIME_TOKENS.has(base)) continue;
    if (knownKeys.size && !knownKeys.has(base)) {
      issues.push({
        where, token: `{{${base}}}`, level: 'error',
        message: namespaceExists(ns, knownKeys)
          ? `Unknown field in "${ns}" — it will print blank`
          : `Unknown namespace "${ns}" — it will print blank`,
      });
    }
  }

  const leftover = text.replace(TOKEN_SPAN, '');
  if (leftover.includes('{{')) {
    issues.push({ where, token: '{{', level: 'error', message: 'Unclosed token — missing }}' });
  }
  (leftover.match(/\{\s*[a-zA-Z_][a-zA-Z0-9_.]*\s*\}/g) || []).forEach((s) => {
    issues.push({ where, token: s, level: 'warn', message: 'Single braces are not a token — use {{ }}' });
  });
  return issues;
};

export const lintTemplate = (tpl, catalogue) => {
  const knownKeys = collectKnownKeys(catalogue);
  const perParagraph = {};
  let issues = [];

  (tpl?.paragraphs || []).forEach((p, idx) => {
    const found = [
      ...lintText(p.heading || '', knownKeys, `§ ${idx + 1} heading`),
      ...lintText(p.body || '', knownKeys, `§ ${idx + 1} body`),
    ];
    if (found.length) perParagraph[idx] = found;
    issues = issues.concat(found);
  });

  const ro = tpl?.render_options || {};
  ['header_left', 'header_center', 'header_right',
    'footer_left', 'footer_center', 'footer_right'].forEach((slot) => {
    issues = issues.concat(lintText(ro[slot] || '', knownKeys, slot.replace('_', ' ')));
  });

  return {
    issues,
    perParagraph,
    errorCount: issues.filter((i) => i.level === 'error').length,
    warnCount: issues.filter((i) => i.level === 'warn').length,
  };
};
